/**
 * Sally release notes — the single source of truth shared by the web app
 * ("What's new" popup) and the API (release-update + re-engagement emails).
 *
 * Add a NEW entry at the TOP of this array whenever a change ships to production,
 * with a short, friendly, user-facing summary (no internal jargon).
 */
export interface ReleaseNote {
  /** Stable id (use the ship date, e.g. "2026-06-24"; bump if multiple/day: "2026-06-24.2"). */
  id: string;
  /** App version this shipped in (matches APP_VERSION). */
  version: string;
  /** Human date shown in the popup. */
  date: string;
  /** One-line headline. */
  title: string;
  /** Friendly bullet points of what changed. */
  items: string[];
  /** When true, this note is hidden from the "What's new" popup + release emails
   * until an admin approves it (its id is added to the approved set). Legacy notes
   * without this flag stay visible as before. */
  pendingApproval?: boolean;
}

/**
 * Whether a release note should be shown to users. Legacy notes (no
 * `pendingApproval`) are always visible; notes flagged pending are visible only
 * once an admin has approved their id.
 */
export function isReleaseNoteVisible(note: ReleaseNote, approvedIds: string[]): boolean {
  return !note.pendingApproval || approvedIds.includes(note.id);
}

/** The release notes currently visible to users, given the admin-approved id set. */
export function visibleReleaseNotes(approvedIds: string[]): ReleaseNote[] {
  return RELEASE_NOTES.filter((n) => isReleaseNoteVisible(n, approvedIds));
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: "2026-09-24",
    version: "2.19.0",
    date: "September 24, 2026",
    title: "Meeting labels — tag, search and ask across your meetings",
    items: [
      "Label your meetings: at the top of any meeting summary (and on the setup screen) you can now add labels like “Migration”, “Security” or “Q3 Renewal”. Tap “Suggest” and Sally proposes labels from what the meeting was about — approve the ones you like or type your own.",
      "Find meetings fast: your dashboard now shows a Labels bar. Click any label to filter to just those meetings, and the search box now matches labels too.",
      "Ask Sally by label: pick one or more labels and hit “Ask about these labels” to ask a question across every meeting with those tags — no need to tick each meeting one by one.",
      "New roles: you can now pick Project Manager, Technical Executive or Business Executive as your role — each comes with tailored hints, prep questions and summary focus for that perspective.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-17",
    version: "2.18.0",
    date: "September 17, 2026",
    title: "Meet your onboarding guide, live captions, and training summaries",
    items: [
      "New floating voice guide: tap the chat bubble in the corner and just talk to Sally. She explains whatever screen you're on, answers “how do I…” questions, and can take you to the right place and point at the exact button to click.",
      "Live captions: on the meeting screen, open the new Captions tab and pick a language — everything being said is captioned in real time and translated into the language you choose (e.g. hear English, read French). Off by default; turn it on whenever you like.",
      "Training / learning meetings: choose “Training / learning” as the meeting type at setup and you'll get a much longer, deeper summary that actually explains everything covered — so anyone can read it afterwards and learn the whole topic.",
      "The voice guide and captions run on your own Gemini key, and you can switch the guide off anytime in Settings → Coaching.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-16",
    version: "2.17.1",
    date: "September 16, 2026",
    title: "Context docs from Drive — now works for Sheets & Slides too",
    items: [
      "Adding a Google Drive link as a context doc now works for Google Sheets, Slides and other Drive files — not just Docs. Previously non-Doc links failed with a confusing error even when you had access.",
      "If a link genuinely can't be imported as text (like a Google Form or a folder), you now get a clear message telling you what to try instead.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-15",
    version: "2.17.0",
    date: "September 15, 2026",
    title: "My ongoing training — your personal learning hub",
    items: [
      "New “My training” tab: get a personalized plan two ways — from your recent meetings, or a fresh curriculum on the latest Google Cloud products for your role (great when you don't have many meetings yet).",
      "Create study materials — podcast, slides, quiz, or an infographic — and see/edit the prompt first. They open right on the page. Make as many as you like, or focus one on a specific topic.",
      "Generate in the background: materials queue up and build one at a time while you keep working. A little progress panel shows what's cooking, and you get a notification the moment each one is ready — no waiting, no error pop-ups.",
      "Pick your podcast length: a ~1-minute quick clip for a fast refresher, the standard 2-3 minute episode, or an 8-12 minute deep dive that walks through each topic in detail.",
      "Choose the language for your study materials — podcasts, slides, quizzes and infographics are generated (and narrated) in the language you pick, and your choice is remembered.",
      "Recommended videos & reading: a preview grid of real, verified, current YouTube videos, Google Cloud blog/docs, and articles matched to your role — videos play in-app, and dead/outdated links are filtered out.",
      "See materials your peers made for your role (shared anonymously), track progress with a done-checklist and progress bar, and tap “Suggest more topics” to keep learning.",
      "Sales roles get a lighter, business-focused plan; technical roles get architecture-level depth.",
      "External & internal meetings: attach documents and (for external) set the client's industry right in the start-meeting popup — Sally tailors live hints and customer stories to it.",
      "New “Outcome CE” role (Outcome Customer Engineer) focused on production ramp, adoption, and consumption growth.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-14",
    version: "2.13.0",
    date: "September 14, 2026",
    title: "Add context documents — and make your hints smarter",
    items: [
      "Attach documents to a meeting and Sally reads them so your live hints reference what's actually in them — battlecards, prior call notes, a customer brief, a security questionnaire, anything useful.",
      "Add docs in three places: when you research a company in Discovery, when you set up a meeting (works for both internal and external meetings), and right in the live meeting from the new “Context docs” panel.",
      "Two easy ways to add: upload a file (PDF, Word, slides, text…) or paste a Google Drive/Docs link. If you're not connected to Drive yet, Sally will ask you to connect the first time.",
      "Add a document mid-call and your hints start using it within seconds — no need to restart. You can also ask about your docs in Quiet ask, and remove any doc you no longer want.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-13",
    version: "2.12.0",
    date: "September 13, 2026",
    title: "Export meetings to Google Drive",
    items: [
      "You can now save any meeting straight to your Google Drive: open a meeting's summary and click “Export to Drive” to write the full transcript plus the summary, action items, and diagrams as a tidy Markdown file — perfect for searching, sharing, or feeding into other tools.",
      "Connect once and it just works. Pick which Drive folder your exports go into under Settings → Profile → Google Drive export (defaults to “Sally Meeting Exports”).",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-10",
    version: "2.11.0",
    date: "September 10, 2026",
    title: "Calendar stays connected + faster pages",
    items: [
      "Your Google Calendar now stays connected — connect once and Sally keeps showing your upcoming meetings without asking you to reconnect again and again.",
      "The dashboard and Clients pages load noticeably faster: your most recent meetings show up right away, with a “Load more” button to pull in older ones.",
      "Clearer start on the live screen — both “Start meeting” and “Share screen” now start the whole meeting (your microphone and screen), so it’s obvious when Sally is listening.",
      "The live meeting panels (Infographic, Diagram, Decision tree, What’s New, Use Cases, Transcript) now stay fully visible and easy to switch between on any screen size.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-07",
    version: "2.10.1",
    date: "September 7, 2026",
    title: "More reliable summaries + clearer microphone warnings",
    items: [
      "Meeting summaries are now much more reliable under heavy load — if our AI is briefly rate-limited, Sally automatically retries instead of leaving you with an empty summary, and it never overwrites a real summary with a blank one.",
      "Clearer microphone help: if Sally stops hearing your mic mid-call (common after a macOS update resets permissions), you'll now get an obvious banner with a one-click “Retry mic” and Mac-specific steps — and Sally keeps transcribing the shared meeting audio in the meantime.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-06",
    version: "2.10.0",
    date: "September 6, 2026",
    title: "Full-length transcripts, sharper coaching, and cleaner summaries",
    items: [
      "Longer meetings now transcribe all the way through — we fixed a case where live transcription could quietly stop after about ten minutes, so your summary now covers the whole call.",
      "Internal meetings get internal-appropriate help: Sally no longer suggests customer-facing moves (like “mention the client”) when you’re meeting your own team.",
      "Technical meetings reliably get an architecture diagram — and if there’s nothing to draw, you’ll see a clear note with a “Generate anyway” button.",
      "Action items always come back in English, even when part of the meeting was in another language.",
      "Smoother Google Calendar: an expired connection now refreshes quietly in the background instead of showing an error.",
      "Your summary now shows when the meeting finished.",
      "Meetings no longer end early during a quiet stretch or when Sally's tab is in the background — the session now stays live much longer before it auto-wraps.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-04.6",
    version: "2.9.7",
    date: "September 4, 2026",
    title: "Security hardening (final pass)",
    items: [
      "Final hardening pass: safer handling of links from research/AI content, stricter link checks, and locked-down meeting edits so only the right fields can ever change.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-04.5",
    version: "2.9.6",
    date: "September 4, 2026",
    title: "Security hardening (cont.)",
    items: [
      "Hardened how uploaded files (tool images, knowledge-base documents, résumés) are served so they can't be abused to run code in your browser.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-04.4",
    version: "2.9.5",
    date: "September 4, 2026",
    title: "More security hardening",
    items: [
      "A second security pass tightened access controls further — locking down account/access settings, résumé file handling, and speech services — so your data and your team's stays protected.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-04.3",
    version: "2.9.4",
    date: "September 4, 2026",
    title: "Security hardening",
    items: [
      "Tightened access controls around meeting screenshots and infographics, live sessions, and third-party plugins, plus safer handling of external links — keeping your meeting data locked to you and the people you share with.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-04.2",
    version: "2.9.3",
    date: "September 4, 2026",
    title: "Your summary keeps trying until it’s ready",
    items: [
      "The summary page now retries automatically if a summary is slow or fails to generate — it keeps working in the background and shows your summary as soon as it’s ready, instead of getting stuck. You can still hit “Retry now” anytime.",
      "Diagrams that finish generating right after a meeting now appear on the summary on their own, without needing a refresh.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-04",
    version: "2.9.2",
    date: "September 4, 2026",
    title: "Never miss that Sally can’t hear your meeting",
    items: [
      "Sally now warns you right away, live, if it’s receiving audio but not picking up any speech — so you can turn on your mic or re-share your tab with “Share tab audio” enabled instead of finding out at the end that nothing was captured.",
      "Upgraded transcription engine for more accurate, more reliable live transcripts across languages.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-03",
    version: "2.9.1",
    date: "September 3, 2026",
    title: "More reliable meeting summaries",
    items: [
      "Your meeting summary now generates reliably right after you end a call — no more occasional stuck 'generating…' screens where the summary never appeared.",
      "If a summary ever hasn't been created yet, opening the summary page now builds it on the spot instead of waiting on a background job.",
      "Behind the scenes: we now catch and report the rare cases where a summary fails to generate, so we can spot and fix them fast.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-09-01",
    version: "2.9.0",
    date: "September 1, 2026",
    title: "Connect your Google Calendar + a richer admin dashboard",
    items: [
      "Connect your Google Calendar and Sally shows your upcoming meetings that include other people, right on your dashboard — start coaching for one in a click. Solo holds and personal blocks are skipped automatically.",
      "Admins: new usage dashboard — total/active users, average meetings per user, simulations, and more at a glance, plus a monthly meetings-over-time chart and a breakdown of users by role.",
      "Admins: a new 'Copy prompt — missing title/role' button so you can look up job titles for just the users who don't have one yet, instead of everyone.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-30",
    version: "2.8.0",
    date: "August 30, 2026",
    title: "Developer platform, plugins, and connect-your-tools",
    items: [
      "New Developer hub + Marketplace plugins: turn on approved plugins that show up right next to Company intel in live meetings and on your summary — and developers can build, test, and submit their own.",
      "Connect your own tools: create a personal access token and use the Sally MCP server to reach your own meetings, transcripts, summaries and tasks from Claude Code, Cursor, or Antigravity — including creating and updating action items. Sign-in handles Google security automatically.",
      "Manage tasks from anywhere: create new action items, change owner/due date, and mark them done through the API/MCP.",
      "Smoother, more reliable Google sign-in.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-27.2",
    version: "2.7.2",
    date: "August 27, 2026",
    title: "Fewer interruptions in live meetings",
    items: [
      "Removed the mid-meeting pop-up that asked you to confirm a detected language change — it no longer interrupts your call. Transcription and Sally's help keep following the conversation as before.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-27",
    version: "2.7.1",
    date: "August 27, 2026",
    title: "Transcription reminder, clearer Terms, and cleaner live hints",
    items: [
      "In external meetings, Sally now shows a clear reminder to let participants know the meeting will be transcribed (only the text is saved — no audio). Click 'I've notified everyone' to dismiss it; your summary then shows whether you confirmed.",
      "The reminder waits patiently in your hints — it never hides new hints and simply moves down as coaching comes in. Internal meetings don't show it.",
      "Updated Terms of Use: text-only processing (no audio stored), how to delete transcripts or turn off saving, and your GDPR/CCPA rights.",
      "Fixed: old hints from a previous session no longer appear before you've started the call.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-25",
    version: "2.7.0",
    date: "August 25, 2026",
    title: "Your own question templates, remembered languages, and clearer summaries",
    items: [
      "Create your own custom-question templates: in Settings → Custom summary you can now add named templates (on top of the built-in role and meeting-type sets), rename them, and rename any individual question. Sally answers them automatically on every meeting summary.",
      "Summaries now open in the language you last chose — pick a language once per meeting and it's remembered next time, with no waiting to re-translate.",
      "Fixed a case where your custom questions could show no answers — they're now matched reliably, so the answers always appear.",
      "The full transcript is easier to read, with clear spacing between each speaker and what they said.",
      "Delete and 'Ask across meetings' are now always visible on your dashboard, so they're easy to find.",
      "On the live screen, a clear note now reminds you that clicking the shared screen captures a screenshot for Sally to read and analyze.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-24.2",
    version: "2.6.1",
    date: "August 24, 2026",
    title: "Ask Sally about the customer you're meeting",
    items: [
      "When you start a meeting from a Discovery or by picking a company, Sally now knows who you're meeting — so in the live Ask you can ask about the customer directly (their tech stack, competitors, cloud challenges, and where Google Cloud fits) and get answers specific to them.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-24",
    version: "2.6.0",
    date: "August 24, 2026",
    title: "New: Marketplace — the best internal tools, in one place",
    items: [
      "Say hello to the Marketplace: a curated directory of handy internal tools for the field, right in the main menu. The tools most relevant to your role show first — Customer Engineers see CE tools up top, sellers see FSR tools first — and you can browse everyone's with the All / CE / FSR tabs.",
      "Each tool has its own icon so you can spot what it does at a glance, and you can filter by category (sandbox, capacity, pricing, and more).",
      "Like the tools you love (♥) so the community's favorites rise to the top, sort by Most liked, and search across everything.",
      "Know a great tool we're missing? Hit 'Suggest a tool' — an admin reviews it and, once approved, it appears for everyone.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-20.5",
    version: "2.5.5",
    date: "August 20, 2026",
    title: "Custom summary answers now work for everyone",
    items: [
      "Your custom questions are now answered automatically on the summary — no personal Gemini key required. They're generated on our side from the transcript (with an Apollo fallback) and appear as soon as the summary loads.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-20.4",
    version: "2.5.4",
    date: "August 20, 2026",
    title: "Updates appear automatically — no more hard refresh",
    items: [
      "New versions of Sally now load automatically — you no longer need to hard-refresh (Ctrl+Shift+R) to see the latest changes.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-20.3",
    version: "2.5.3",
    date: "August 20, 2026",
    title: "Translate any summary, Sr. Cloud SE = technical, role on internal meetings",
    items: [
      "Translate the summary and the client email into any language from a picker on the summary — it now works for every meeting (not only ones with a preset language).",
      "Sr. Cloud SE is now treated as a technical engineer role — you'll get technical hints, architecture diagrams, and a technical infographic, just like a Customer Engineer.",
      "Starting an internal meeting now lets you pick your role (and prep questions) before you go live.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-20.2",
    version: "2.5.2",
    date: "August 20, 2026",
    title: "Summary media shows reliably (infographics, diagrams, screenshots)",
    items: [
      "Fixed: meeting infographics, diagrams, and screenshots sometimes didn't appear on the summary even though they were captured during the call — they now load through the app, so ad blockers / strict networks can't hide them.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-20",
    version: "2.5.0",
    date: "August 20, 2026",
    title: "Ask across meetings, live role/focus controls, translated summaries & more",
    items: [
      "Ask across several meetings at once: select meetings on the dashboard and hit 'Ask about selected' to chat over all of them (grounded in their transcripts).",
      "Bulk-delete meetings from the dashboard, and a bigger, safer select checkbox so you never open a meeting by accident.",
      "Tune coaching live: pick your Role, your Question set, and a Focus type (Technical / Executive / Deep dive / Project manager) right in the live panel — they shape hints, follow-ups, and the infographic. Project-manager focus also adds time & agenda notes to the summary.",
      "See the summary and the client email in your meeting's language with one click (translated on demand).",
      "Generate an infographic from the summary any time — meetings that didn't make one live now can, and one is created automatically when missing.",
      "End-of-meeting popup: a simple checkbox (on by default) to share this meeting's product bug/feature insights with Google product managers.",
      "Cleaner Tasks and Clients: Tasks are grouped by due date with company logos + a search box; the Clients page is now a searchable table with logos and quick actions.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-17",
    version: "2.4.0",
    date: "August 17, 2026",
    title: "Discovery workspace, company logos everywhere & richer company intel",
    items: [
      "New Discovery workspace: deep-research any company before your meeting — founders, recent news, competitors, market, hiring, and where Google Cloud fits. Your discoveries are pinned on the dashboard and you can start a meeting straight from one.",
      "Pick the right company, fast: start typing and choose the correct company (with its logo) from a live list — handy when several companies share a name. Real company and competitor logos now show throughout.",
      "Company intel on the setup page now shows the full Research / Billing / Growth view, the same as in a live meeting.",
      "Billing charts are back and better: Total monthly spend, Top services over time, and Cost distribution — and billing starts loading the moment you pick a company, so it's ready when you open the tab.",
      "Summary custom questions fill in on their own right after the meeting — no need to hit Refresh.",
      "Dashboard tidy-up: your discoveries now sit just above meeting history.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-16",
    version: "2.3.0",
    date: "August 16, 2026",
    title: "Membership, smarter Quick Ask, on-demand infographics & language control",
    items: [
      "Say hello to membership! Share feedback and you become a Silver member; our top contributors are made Gold — you'll see your badge right on your dashboard.",
      "New 'Contributors' link in the footer: send us an idea or a prompt to improve Sally and we'll add you to the list.",
      "Quick Ask upgrade: choose a Short or Deep answer, ask for a deeper answer on the same question with one click, and Deep answers now come with a relevant infographic you can enlarge. The ask box is roomier too.",
      "Infographics on demand: hit 'Generate now' any time (in addition to the automatic ones). For Customer Engineers they're now technical architecture visuals.",
      "Customer Engineers get noticeably deeper, more technical answers — both in live hints and Quick Ask.",
      "Meeting language, your way: pick the spoken language or let Sally auto-detect it — once set it stays stable for the whole meeting (no more flip-flopping), and Sally asks before switching if it hears a different language. Your choice is remembered.",
      "End of meeting: get three suggested names to pick from (or type your own), always in English.",
      "Company intel Billing & Growth load reliably again.",
      "Sally now runs on Google's newest Gemini 3.7 Flash model for sharper, more current answers.",
      "Meeting setup is simpler: the extra 'Website' field is gone — just type the company name and Sally does the research.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-12",
    version: "2.2.2",
    date: "August 12, 2026",
    title: "Summary & live-coaching fixes",
    items: [
      "Your custom questions and their answers now load on the summary on their own — no need to hit Re-run first.",
      "Meeting diagrams show up on the summary right away instead of only after a page refresh.",
      "Dark mode: the company-intel tabs and the meeting-chat boxes are readable again (no more white-on-gray).",
      "Live coaching: a new hint no longer scrolls you back to the top while you're reading earlier ones — you'll see a '↑ New hint' button to jump up when you're ready.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-11.3",
    version: "2.2.1",
    date: "August 11, 2026",
    title: "Fewer Google prompts for billing",
    items: [
      "Company Billing & Growth now remember your Google connection and refresh it quietly in the background, so you're not asked to reconnect again and again in the same session.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-11.2",
    version: "2.2.0",
    date: "August 11, 2026",
    title: "Company intel in live meetings, smarter diagrams, and chat with your meeting",
    items: [
      "Company intel now lives right on the live meeting screen (Research / Billing / Growth), tucked under My notes. In an internal meeting there's a search box so you can look up any company on the fly.",
      "New 'Talk with your meeting' on the summary: ask questions and Sally answers from the transcript, participants, and summary — and your questions & answers are saved for next time.",
      "Ask questions about the raw transcript directly from the Full transcript tab.",
      "Architecture diagrams now render on the first try — they auto-retry behind the scenes instead of coming up blank, and a diagram glitch that could interrupt a live meeting is fixed.",
      "Company name search no longer jumps ahead while you type — it looks up the company when you press Enter, click, or pick a suggestion.",
      "Meeting infographics are more on-topic, and you stay signed in without being asked to log in again.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-11",
    version: "2.1.1",
    date: "August 11, 2026",
    title: "Stay signed in",
    items: [
      "Fixed the annoying loop where Sally would ask you to sign in again on a new tab or after your laptop woke from sleep — your session is now remembered reliably, and a briefly-expired login refreshes itself in the background instead of bouncing you to the login screen.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-10",
    version: "2.1.0",
    date: "August 10, 2026",
    title:
      "Company billing & growth load automatically, prettier charts, and a bigger results view",
    items: [
      "Sign in to Sally once — that's it. Company Billing & Growth no longer ask you to connect Google a second time.",
      "Billing & Growth now load on their own the moment you pick a company, right alongside the research — no button to click, no waiting to start it.",
      "Billing charts got a refresh: cleaner spend trends, a cost-by-service breakdown ring, and dollar amounts on hover.",
      "Need a closer look? Hit Expand on the company intel panel to give the results and charts more room; the setup fields tuck aside until you collapse it again.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-09",
    version: "2.0.4",
    date: "August 9, 2026",
    title: "Browse past summary versions + deep dives always draw diagrams",
    items: [
      "Opening a previous summary version now just shows it — read-only, with a Restore button and a Back-to-latest shortcut. No more accidental re-analysis when you only wanted to look.",
      "Technical deep dive now always produces the technical diagrams (architecture + decision tree), even when the earlier summary had none.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-06.4",
    version: "2.0.3",
    date: "August 6, 2026",
    title: "Fix: reach the summary of a resumed meeting",
    items: [
      "A meeting you continued (resumed) and then closed no longer gets stuck on the live screen — it opens its summary from the dashboard, and there's now a shortcut to the summary from the live screen header.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-06.3",
    version: "2.0.2",
    date: "August 6, 2026",
    title: "Custom-question sets apply live, expandable summary diagrams, smarter diagram button",
    items: [
      "The prep-question set you pick for a meeting (e.g. CE Infra or CE AI) now shows and gets answered in the live 'My questions' tab and on the summary — not just your default questions.",
      "On the summary, click any diagram to open it full-screen.",
      "The live Diagram / Decision-tree tabs now wait until there's enough of the conversation before offering to generate — and show a green dot on the tab when they're ready.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-06.2",
    version: "2.0.1",
    date: "August 6, 2026",
    title: "Diagram fixes for Customer Engineers",
    items: [
      "Customer Engineers now reliably land on the Diagram tab first in a live meeting — it follows the role you picked for that meeting, not just your profile role.",
      "The meeting summary now shows every diagram created during the call (architecture and decision tree), plus a freshly generated summary diagram for each type.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-06",
    version: "2.0.0",
    date: "August 6, 2026",
    title: "Customer Engineer mode: role-aware coaching, live diagrams, and company intel",
    items: [
      "Pick your role (CE Security, CE Infra, CE AI, CE AppDev, CE Data, and more) — hints, follow-up questions, and the summary now lean into your specialty while still helping across the board. Choose it per meeting on setup; Sally remembers your last choice.",
      "New live Diagram tab draws an architecture diagram of what's being discussed, and a separate Decision tree tab charts the decision/process flow. Generate on demand (with a couple of quick clarifying questions), add more, and expand any diagram full-screen. All diagrams also appear on the summary.",
      "Company intel right in the meeting popup: a Research profile (what they do, stack, hiring signal), plus Billing and Growth tabs powered by your CRM — with the billing tables rendered as clear graphs.",
      "Set a goal/topics for the meeting and Sally reminds you (in an orange card) about anything you haven't covered yet.",
      "Ready-made prep-question sets per role and meeting type — pick one per meeting, edit them, and your changes are saved.",
      "Continue a meeting you closed by mistake or dropped on a bad connection — pick up right where it stopped, from the summary or your dashboard.",
      "Export or copy the full transcript (copy, .txt, or PDF).",
      "Admins can add any public web page to the knowledge base (not just Google Docs), and knowledge-base hints no longer show broken document links.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-03.2",
    version: "1.9.0",
    date: "August 3, 2026",
    title: "Talk in any language, manage your clients, and Terms of Use",
    items: [
      "Meetings are now transcribed in whatever language is actually spoken — French, German, Spanish and more stay in their own language instead of being forced into Hebrew. The full transcript can be filtered by each language that came up.",
      "Read-aloud and dictation now work across all supported languages, following your meeting's language.",
      "New client management: rename a client, view all of a client's tasks, and delete a client. Deleting shows you exactly which tasks will be removed and asks you to confirm first — your meetings stay in your history.",
      "Added a “Sally Terms of Use” link in the footer covering consent, intended use, data handling, and more.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-03",
    version: "1.8.2",
    date: "August 3, 2026",
    title: "Dashboard layout fix",
    items: [
      "Fixed a dashboard layout glitch where the page could shift sideways — leaving a big empty space on the left and cutting off the right edge — and the meeting history could get squeezed into a narrow column. Everything now sits in its proper place again, at any window width.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-02.2",
    version: "1.8.1",
    date: "August 2, 2026",
    title: "Faster meeting list, richer interviews, technical deep-dive re-run",
    items: [
      "Your meetings list now loads faster and pages 30 at a time, with a Previous/Next pager. Search still looks across all your meetings, not just the current page.",
      "New meeting-type filter (Sales / Technical / Interview / Onboarding / Review) so you can jump straight to what you need — interviews included.",
      "Interviews dashboard shows more at a glance: candidate name, interview type, role and level — plus a search box. Candidate name is now required and appears in the interview's name.",
      "On any meeting summary you can now “Re-run with technical deep dive” for a deeper architecture/components/integrations write-up (in addition to the normal re-run).",
      "Better automatic meeting names at the end of a call — more specific to what was actually discussed, in the meeting's language.",
      "Coaching now replies in the language you're speaking (e.g. French calls get French hints), and Quick ask answers in your question's language.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-08-02",
    version: "1.8.0",
    date: "August 2, 2026",
    title: "Interviews: better Hebrew, a clearer live screen, and read-aloud",
    items: [
      "Much better Hebrew transcription during interviews — it now follows the spoken language more closely and drops far fewer words.",
      "Redesigned live interview screen: the right side keeps Ask questions, Transcript and a new “Quick ask” (ask Gemini privately) as tabs on top, with the live Score and Tips always visible underneath.",
      "The Live summary now loads a summary of the candidate's résumé the moment you open the screen, then keeps updating from their answers as the interview goes.",
      "Open the candidate's résumé right from the live screen in a pop-up (press Esc or ✕ to close), and your notes now sit under it.",
      "Each answer gets a quick score with both a follow-up and a deeper “in-depth” follow-up suggestion.",
      "After the interview you can view the résumé, your private notes and the full transcript from the evaluation page — and have the summary read aloud, or dictate notes straight into it.",
      "If an interview records no audio, the evaluation page now explains why (check your mic and the candidate's shared audio) instead of showing a generic error.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-07-28",
    version: "1.7.1",
    date: "July 28, 2026",
    title: "Interviews: reliable access to your evaluations",
    items: [
      "Fixed a glitch where the Interviews section could briefly bounce you back to the dashboard right after opening the app — it now waits for your profile to load before deciding.",
      "Admins can now open and review any interviewer's evaluation from the dashboard (read-only), with interviews clearly tagged so they're easy to spot.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-07-21",
    version: "1.7.0",
    date: "July 21, 2026",
    title: "Use Cases, flexible emails, key onboarding & lots of polish",
    items: [
      "New “Use Cases” tab in the live meeting (next to What's New): as you talk, Sally suggests a relevant Google Cloud customer story you can bring up with the client, with a 1-3 relevance rating.",
      "Follow-up emails, your way: pick exactly what to include with checkboxes (summary, action items, links, and more), and hit Send to open it pre-filled in Gmail. The template is tighter and drops the redundant customer background.",
      "Smoother first run: new users get a clear step to add their AI Studio API key, with a “how to get one” video — or tell us you don't have one yet and keep going.",
      "The meeting summary diagram is now available on every meeting for everyone, and you can zoom it or open it full-screen.",
      "Live hints now carry keyword tags — click a tag (on a hint or above Ask) to filter hints to just that topic.",
      "Fixes: duplicate participants are cleaned up more reliably, and renaming a meeting no longer jumps you into it when you press Space.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-07-15",
    version: "1.6.0",
    date: "July 15, 2026",
    title: "Technical meetings, editable summaries & more reliable transcription",
    items: [
      "New “Technical meeting” type in setup — turn it on and Sally draws a live architecture diagram tab during the call (with a red dot when a new one is ready) and includes the diagram in your summary.",
      "More reliable transcription — the last thing said before a pause is no longer dropped, so the transcript captures the full conversation.",
      "Better participant capture — Sally keeps reading the Meet roster through the call, so people who join late are picked up too.",
      "Edit the opportunity and the company right on the summary when the auto-detected text isn't quite right.",
    ],
    pendingApproval: true,
  },
  {
    id: "2026-07-10.3",
    version: "1.5.5",
    date: "July 10, 2026",
    title: "Cleaner participant lists",
    items: ["Participant lists no longer show the same person multiple times."],
    pendingApproval: true,
  },
  {
    id: "2026-07-10.2",
    version: "1.5.4",
    date: "July 10, 2026",
    title: "Richer summaries: edit the company, longer recap, architecture diagram",
    items: [
      "The meeting summary is now a fuller, more detailed recap of the whole call instead of a few sentences.",
      "You can now correct the company name right on the summary — handy when the auto-detected company is wrong or the customer is too new to have public data. Click Edit on the Meeting summary card.",
      "If a system architecture was discussed, the summary now shows a diagram of it.",
      "Fixed duplicated participant names (the same person no longer appears multiple times).",
    ],
  },
  {
    id: "2026-07-10",
    version: "1.5.3",
    date: "July 10, 2026",
    title: "Cleaner hints: fewer forced comparisons, grouped by topic",
    items: [
      "Live hints now show a side-by-side comparison only when it's genuinely relevant — when a competitor is named or you're explicitly weighing options — instead of adding one to almost every hint.",
      "Hints about the same topic now combine into a single card you can page through, instead of piling up as separate cards.",
      "Better participant names — Sally picks up who's on the call from the conversation, and no longer overwrites a captured name with a generic 'Client'.",
    ],
  },
  {
    id: "2026-07-08.3",
    version: "1.5.2",
    date: "July 8, 2026",
    title: "Summary shows the right company",
    items: [
      "The meeting summary now uses the company you picked before the call, instead of sometimes showing another company that was only mentioned during the conversation.",
    ],
  },
  {
    id: "2026-07-08.2",
    version: "1.5.1",
    date: "July 8, 2026",
    title: "Simpler live Ask bar",
    items: [
      "Removed the separate 'Urgent help' button — just type your question in Ask and Sally answers instantly from the live conversation.",
    ],
  },
  {
    id: "2026-07-08",
    version: "1.5.0",
    date: "July 8, 2026",
    title: "Tune comparisons + smarter question suggestions",
    items: [
      "New Settings → Coaching control for comparison tables in hints: keep them Frequent, or switch to 'Only when clearly relevant' so you only get a comparison on an explicit competitor or a direct comparison question.",
      "Sally now spots questions you ask in meeting after meeting and suggests adding them to your saved questions — look for the prompt in Settings, on the meeting summary, and on your dashboard.",
    ],
  },
  {
    id: "2026-07-07.6",
    version: "1.4.4",
    date: "July 7, 2026",
    title: "Faster Ask answers",
    items: [
      "Quiet Ask now starts streaming the answer much sooner — it looks up the knowledge base and the live web in parallel instead of one after the other, so you're no longer waiting ~10s for a reply.",
    ],
  },
  {
    id: "2026-07-07.5",
    version: "1.4.3",
    date: "July 7, 2026",
    title: "More side-by-side comparisons in hints",
    items: [
      "Live hints now surface a Google-vs-alternative comparison table in many more moments — whenever the client asks how something works, which option is better, about pricing or capabilities, or mentions any product or category (not just a named competitor).",
    ],
  },
  {
    id: "2026-07-07.4",
    version: "1.4.2",
    date: "July 7, 2026",
    title: "Answers match your question's language",
    items: [
      "Quiet Ask and live answers now reply in the language you asked in — ask in English, get English; ask in Hebrew, get Hebrew — even when the rest of the call is in another language.",
    ],
  },
  {
    id: "2026-07-07.3",
    version: "1.4.1",
    date: "July 7, 2026",
    title: "Sally remembers your Gemini key",
    items: [
      "Enter your Gemini API key once and Sally remembers it on your account — no need to re-enter it when you switch devices or come back later.",
      "Simplified the key setup screen: just paste your key and go.",
    ],
  },
  {
    id: "2026-07-07.2",
    version: "1.4.0",
    date: "July 7, 2026",
    title: "Choose your hint strategy",
    items: [
      "Hints felt too fast and too many for some, just right for others — so you can now pick a strategy: Live (frequent, the current behavior), Balanced (slower, only when a hint clearly helps), or Focused (only the critical moments — a competitor, a real objection, a strong buying signal).",
      "Switch strategy right from the live meeting header, mid-call — your last choice sticks as your default next time.",
      "Screen-share “Screen insight” hints are now quieter unless you're on Live: Balanced shows them only for competitors or pricing, Focused only for competitors — so they help instead of distract.",
      "Set your default anytime in Settings → Coaching.",
    ],
  },
  {
    id: "2026-07-07",
    version: "1.3.1",
    date: "July 7, 2026",
    title: "Live hints always in your meeting language",
    items: [
      "Live hints now stay in the language you set for the meeting (e.g. German or Hebrew) — no more hints flipping to English mid-call.",
      "If you leave the language on Auto, Sally locks onto the spoken language once it's detected and keeps hints consistent for the rest of the call.",
    ],
  },
  {
    id: "2026-07-02",
    version: "1.3.0",
    date: "July 2, 2026",
    title: "Live question tracking, sharing & notifications",
    items: [
      "New “My questions” tab in the live meeting: see your custom questions answered in real time as the conversation unfolds, with a progress counter (e.g. 3/6 answered).",
      "Share a meeting summary with teammates by email — Sally remembers who you've shared with before and auto-completes them next time.",
      "A notifications bell in the header shows when someone shares a summary with you; open it to jump straight to their meeting.",
      "Just approved for access? You'll now get a welcome email with a link to the how-to video and a button to start using Sally.",
    ],
  },
  {
    id: "2026-06-24.2",
    version: "1.2.0",
    date: "June 24, 2026",
    title: "Polish: feedback, participants, pins & What's-new",
    items: [
      "Smaller, clearer “Rate Sally” box on the summary.",
      "Custom summary now appears under Action items, and can be turned on/off in Settings.",
      "One editable Participants list on the summary — edit names and add people in one place.",
      "Quick meeting: you can Skip the company name and add it after the meeting.",
      "Live hints: only one hint stays pinned at a time.",
      "This What's-new popup now scrolls through past updates with version + date.",
    ],
  },
  {
    id: "2026-06-24",
    version: "1.1.9",
    date: "June 24, 2026",
    title: "Custom summaries, company intel & feedback",
    items: [
      "New “Custom summary”: set your own questions in Settings and Sally answers them from each meeting — you can edit the answers and questions right on the summary.",
      "Edit the meeting summary text and the attendee names directly on the summary page.",
      "Company intel before you meet: type a company when starting a quick or new meeting and Sally auto-researches what they do, founders, funding and size — now enriched with Apollo.io.",
      "Wrong company details? Edit the profile (name, description, founders…) right on the card, or re-research it — your corrections stick.",
      "Smarter meeting names: the end-of-meeting name suggestion now always includes the client company name.",
      "Cleaner live hints: hints on the same topic (e.g. several angles of one competitor comparison) now collapse into one card with ‹ › arrows and a count of how many more there are — new topics still appear on top, and hints stay just as fast.",
      "Rate Sally and leave feedback right from the meeting summary — now a clear, highlighted box with the stars and a roomy comment field shown up front.",
    ],
  },
];

export const LATEST_RELEASE_ID = RELEASE_NOTES[0]?.id ?? "";
